export type TableConfig = {
  key: string;
  label: string;
};
type FinePrintSectionNames =
  | "overview"
  | "doctorVisits"
  | "prescriptionDrugs"
  | "emergencies"
  | "hospitalCoverage"
  | "pregnancyRelated"
  | "labsTestsScans"
  | "other";
type FinePrintSections = Record<FinePrintSectionNames, Array<TableConfig>>;
export const finePrintLabels: Array<[FinePrintSectionNames, string]> = [
  ["overview", "Overview"],
  ["doctorVisits", "Doctors"],
  ["prescriptionDrugs", "Prescriptions"],
  ["emergencies", "Emergencies"],
  ["hospitalCoverage", "Hospitalization"],
  ["pregnancyRelated", "Pregnancy"],
  ["labsTestsScans", "Tests & Scans"],
  ["other", "Other"],
];
export const finePrintSections: FinePrintSections = {
  overview: [
    {
      label: "Office Visit",
      key: "pcp_visit",
    },
    {
      label: "Specialist Visit",
      key: "specialist_visit",
    },
    {
      label: "Generic Drugs",
      key: "generic_drugs",
    },
    {
      label: "Brand Drugs",
      key: "brand_drugs",
    },
    {
      label: "Emergency Room",
      key: "emergency_room",
    },
    {
      label: "Hospitalization",
      key: "hospitalization",
    },
  ],
  doctorVisits: [
    {
      label: "Office Visit",
      key: "pcp_visit",
    },
    {
      label: "Specialist Visit",
      key: "specialist_visit",
    },
    {
      label: "Annual Physical",
      key: "periodic_exam",
    },
    {
      label: "Annual OB/GYN Checkup",
      key: "obgyn_exam",
    },
    {
      label: "Well Baby Care",
      key: "baby_care",
    },
  ],
  // Note that the prescription drugs value here should only be used when you
  // don't care about the specific drug tiers that the plan has.
  prescriptionDrugs: [
    {
      label: "Generic Drugs",
      key: "generic_drugs",
    },
    {
      label: "Brand Drugs",
      key: "brand_drugs",
    },
    {
      label: "Drug Deductible",
      key: "separate_rx_deductible",
    },
  ],
  emergencies: [
    {
      label: "Emergency Room",
      key: "emergency_room",
    },
    {
      label: "Ambulance",
      key: "ambulance",
    },
    {
      label: "Urgent Care",
      key: "urgent_care",
    },
  ],
  hospitalCoverage: [
    {
      label: "Hospitalization",
      key: "hospitalization",
    },
    {
      label: "Outpatient Surgery",
      key: "surgery",
    },
    {
      label: "Physical Therapy",
      key: "physical_therapy",
    },
  ],
  pregnancyRelated: [
    {
      label: "Labor & Delivery",
      key: "labor_facility",
    },
    {
      label: "Pre & Post Natal Visits",
      key: "prenatal_office_visit",
    },
    {
      label: "Infertility",
      key: "infertility",
    },
  ],
  labsTestsScans: [
    {
      label: "X-Ray",
      key: "xray",
    },
    {
      label: "MRI/CT/PET",
      key: "mri",
    },
    {
      label: "Labs and Tests",
      key: "labs",
    },
  ],
  other: [
    {
      label: "Chiropractic",
      key: "chiropractic",
    },
    {
      label: "Mental Health",
      key: "mental_health",
    },
    {
      label: "Substance Abuse",
      key: "substance_abuse",
    },
  ],
};
export const splitHospitalFinePrintSections: FinePrintSections = {
  overview: [
    {
      label: "Office Visit",
      key: "pcp_visit",
    },
    {
      label: "Specialist Visit",
      key: "specialist_visit",
    },
    {
      label: "Generic Drugs",
      key: "generic_drugs",
    },
    {
      label: "Brand Drugs",
      key: "brand_drugs",
    },
    {
      label: "Emergency Room",
      key: "emergency_room",
    },
    {
      label: "Hospitalization - Hospital",
      key: "hospitalization",
    },
    {
      label: "Hospitalization - Doctor's Fees",
      key: "hospitalization_physician",
    },
  ],
  doctorVisits: [
    {
      label: "Office Visit",
      key: "pcp_visit",
    },
    {
      label: "Specialist Visit",
      key: "specialist_visit",
    },
    {
      label: "Annual Physical",
      key: "periodic_exam",
    },
    {
      label: "Annual OB/GYN Checkup",
      key: "obgyn_exam",
    },
    {
      label: "Well Baby Care",
      key: "baby_care",
    },
  ],
  // Note that the prescription drugs value here should only be used when you
  // don't care about the specific drug tiers that the plan has.
  prescriptionDrugs: [
    {
      label: "Generic Drugs",
      key: "generic_drugs",
    },
    {
      label: "Brand Drugs",
      key: "brand_drugs",
    },
    {
      label: "Drug Deductible",
      key: "separate_rx_deductible",
    },
  ],
  emergencies: [
    {
      label: "Emergency Room",
      key: "emergency_room",
    },
    {
      label: "Ambulance",
      key: "ambulance",
    },
    {
      label: "Urgent Care",
      key: "urgent_care",
    },
  ],
  hospitalCoverage: [
    {
      label: "Hospitalization - Hospital",
      key: "hospitalization",
    },
    {
      label: "Hospitalization - Doctor's Fees",
      key: "hospitalization_physician",
    },
    {
      label: "Outpatient Surgery - Hospital",
      key: "surgery",
    },
    {
      label: "Outpatient Surgery - Surgeon",
      key: "surgery_staff",
    },
    {
      label: "Physical Therapy",
      key: "physical_therapy",
    },
  ],
  pregnancyRelated: [
    {
      label: "Labor & Delivery - Hospital",
      key: "labor_facility",
    },
    {
      label: "Labor & Delivery - Delivery",
      key: "labor",
    },
    {
      label: "Pre & Post Natal Visits",
      key: "prenatal_office_visit",
    },
    {
      label: "Infertility",
      key: "infertility",
    },
  ],
  labsTestsScans: [
    {
      label: "X-Ray",
      key: "xray",
    },
    {
      label: "MRI/CT/PET",
      key: "mri",
    },
    {
      label: "Labs and Tests",
      key: "labs",
    },
  ],
  other: [
    {
      label: "Chiropractic",
      key: "chiropractic",
    },
    {
      label: "Mental Health",
      key: "mental_health",
    },
    {
      label: "Substance Abuse",
      key: "substance_abuse",
    },
  ],
};
export const mapSectionsToLabels = (sections: FinePrintSections) => {
  const mapping = Object.values(sections).reduce((acc, current) => {
    current.forEach((item) => {
      acc[item.key] = item.label;
    });
    return acc;
  }, {});
  mapping.baby_care = "Maternity Care"; // Pre and Post Natal Visits

  return mapping;
};
export const mapFinePrintToLabels = mapSectionsToLabels(finePrintSections);
export default finePrintSections;
